import * as Yup from 'yup'
import { ContratoType, createValidation } from '../../../../../../../../_metronic/helpers'
import { TFunction } from '_metronic/i18n/Metronici18n'

export type EmpresaSchemaMode = 'empresa' | 'cliente'

export const createEmpresaSchema = (t: TFunction, mode: 'empresa' | 'cliente') => {
  const v = createValidation(t)

  return Yup.object().shape(
    {
      id: Yup.number().notRequired(),

      limiteUnidades: Yup.number().nullable().notRequired(),

      cnpj: Yup.string().trim().required('CNPJ é obrigatório'),
      slug: Yup.string().trim().notRequired(),

      nomeFantasia: Yup.string().trim().required('Nome fantasia é obrigatório'),
      nome: Yup.string().trim().required('Nome é obrigatório'),

      tipoUnidade: Yup.object().nullable().required('Tipo de estabelecimento é obrigatório'),

      responsavel: Yup.object()
        .shape({
          cpf: Yup.string()
            .trim()
            .matches(/^\d{3}\.\d{3}\.\d{3}-\d{2}$/, 'CPF inválido')
            .nullable()
            .notRequired(),
          primeiroNome: Yup.string()
            .trim()
            .when('cpf', {
              is: (cpf: string) => !!cpf && cpf.trim().length > 0,
              then: (schema) => schema.required('Nome do responsável é obrigatório'),
              otherwise: (schema) => schema.notRequired(),
            }),
          sobreNome: Yup.string()
            .trim()
            .when('cpf', {
              is: (cpf: string) => !!cpf && cpf.trim().length > 0,
              then: (schema) => schema.required('Sobrenome do responsável é obrigatório'),
              otherwise: (schema) => schema.notRequired(),
            }),
          email: Yup.string()
            .trim()
            .email('Formato inválido')
            .nullable()
            .when('cpf', {
              is: (cpf: string) => !!cpf && cpf.trim().length > 0,
              then: (schema) => schema.required('E-mail do responsável é obrigatório'),
              otherwise: (schema) => schema.notRequired(),
            }),
          telefone: Yup.string().nullable().notRequired(),
          nascimento: Yup.string()
            .nullable()
            .when('cpf', {
              is: (cpf: string) => !!cpf && cpf.trim().length > 0,
              then: (schema) => schema.required('Data de nascimento do responsável é obrigatória'),
              otherwise: (schema) => schema.notRequired(),
            }),
          sexo: Yup.object()
            .nullable()
            .when('cpf', {
              is: (cpf: string) => !!cpf && cpf.trim().length > 0,
              then: (schema) => schema.required('Sexo do responsável é obrigatório'),
              otherwise: (schema) => schema.notRequired(),
            }),
          conselho: Yup.object()
            .nullable()
            .when('cpf', {
              is: (cpf: string) => !!cpf && cpf.trim().length > 0,
              then: (schema) => schema.required('Conselho do responsável é obrigatório'),
              otherwise: (schema) => schema.notRequired(),
            }),
          crm: Yup.string()
            .trim()
            .nullable()
            .when('cpf', {
              is: (cpf: string) => !!cpf && cpf.trim().length > 0,
              then: (schema) => schema.required('Registro no conselho é obrigatório'),
              otherwise: (schema) => schema.notRequired(),
            }),
          uf: Yup.object()
            .nullable()
            .when('cpf', {
              is: (cpf: string) => !!cpf && cpf.trim().length > 0,
              then: (schema) => schema.required('UF do conselho é obrigatória'),
              otherwise: (schema) => schema.notRequired(),
            }),
          rqe: Yup.string().trim().nullable().notRequired(),
        })
        .notRequired(),

      especialidades: Yup.array()
        .nullable()
        .notRequired()
        .of(
          Yup.object().shape({
            label: Yup.string().required(),
            value: Yup.number().required(),
            tempoConsulta: Yup.number()
              .transform((value, originalValue) =>
                originalValue === '' || originalValue === null ? undefined : value
              )
              .notRequired()
              .min(1, 'Tempo de duração deve ser no mínimo 1 minuto'),
            gruposTelegram: Yup.array()
              .of(
                Yup.object({
                  tipoAtendimento: Yup.object()
                    .nullable()
                    .required('Selecione um tipo de atendimento'),
                  grupoTelegram: Yup.string().trim().notRequired(),
                })
              )
              .default([]),
          })
        ),

      inicioContrato: Yup.string().nullable().notRequired(),
      terminoContrato: Yup.string().nullable().notRequired(),

      sus: Yup.boolean()
        .required('Selecione uma opção')
        .oneOf([true, false], 'Selecione uma opção')
        .nullable(),
      // Obrigatórios quando atende SUS, ou quando o outro campo do par foi preenchido
      nomeCnes: Yup.string()
        .trim()
        .when(['sus', 'cnes'], {
          is: (sus: boolean, cnes?: string) => sus || !!cnes?.trim(),
          then: (schema) => schema.required('Nome do CNES obrigatório'),
          otherwise: (schema) => schema.notRequired(),
        }),
      codigoSes: Yup.string()
        .trim()
        .matches(/^\d{7}$/, {
          message: 'O código do Núcleo deve ter 7 dígitos numéricos',
          excludeEmptyString: true,
        }),
      cnes: Yup.string()
        .trim()
        .matches(/^\d{7}$/, { message: 'Formato inválido', excludeEmptyString: true })
        .when(['sus', 'nomeCnes'], {
          is: (sus: boolean, nomeCnes?: string) => sus || !!nomeCnes?.trim(),
          then: (schema) => schema.required('CNES obrigatório'),
          otherwise: (schema) => schema.notRequired(),
        }),

      tiposAtendimento: Yup.array()
        .of(Yup.object())
        .min(1, 'Selecione ao menos um tipo de atendimento'),

      tiposContrato: Yup.array().of(Yup.object()).min(1, 'Selecione ao menos um tipo de contrato'),

      hilab: Yup.object()
        .shape({
          ativo: Yup.boolean().required(),
          oauthUser: Yup.string()
            .trim()
            .when('ativo', {
              is: true,
              then: (campo) => campo.required('Usuário OAuth é obrigatório'),
              otherwise: (campo) => campo.notRequired(),
            }),
          oauthPassword: Yup.string()
            .trim()
            .when(['ativo', 'senhaCadastrada'], {
              is: (ativo: boolean, senhaCadastrada: boolean) => ativo && !senhaCadastrada,
              then: (campo) => campo.required('Senha OAuth é obrigatória'),
              otherwise: (campo) => campo.notRequired(),
            }),
          webhookHeaderName: Yup.string().trim().notRequired(),
          webhookSecret: Yup.string().trim().notRequired(),
          senhaCadastrada: Yup.boolean().required(),
          segredoCadastrado: Yup.boolean().required(),
        })
        .test('par-webhook', function (hilab) {
          const cabecalho = hilab?.webhookHeaderName?.trim()
          const segredo = hilab?.webhookSecret?.trim()
          const segredoCadastrado = !!hilab?.segredoCadastrado
          if (segredo && !cabecalho) {
            return this.createError({
              path: `${this.path}.webhookHeaderName`,
              message: 'Nome do cabeçalho é obrigatório',
            })
          }
          if (cabecalho && !segredo && !segredoCadastrado) {
            return this.createError({
              path: `${this.path}.webhookSecret`,
              message: 'Segredo do webhook é obrigatório',
            })
          }
          return true
        }),

      email: Yup.string().email('Formato inválido').nullable().notRequired(),
      telefone: Yup.string()
        .matches(
          /^\(\d{2}\) (\d )?\d{4}-\d{4}$/,
          'Formato deve ser (00) 0000-0000 ou (00) 0 0000-0000'
        )
        .nullable()
        .when('tiposContrato', {
          is: (contratos: { value: ContratoType; label: string }[] | null | undefined) =>
            contratos?.some(
              (c) =>
                c.value === ContratoType.Link_Celular || c.value === ContratoType.Link_Plataforma
            ),
          then: (schema) => schema.required('Telefone é obrigatório'),
          otherwise: (schema) => schema.notRequired().nullable(),
        }),

      ...(mode === 'cliente'
        ? {
            empresaMae: Yup.object().nullable().required(v.requiredField('empresa', 'select')),
          }
        : {}),

      observacoes: Yup.array()
        .of(Yup.string().trim().required('Necessário preencher observação'))
        .default([]),

      apiWebhook: Yup.string().trim().notRequired(),

      dominiosIntegracao: Yup.array()
        .of(
          Yup.string()
            .trim()
            .required('Necessário preencher o domínio')
            .matches(
              /^([a-z0-9]([a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}$/i,
              'Informe apenas o domínio (ex: cliente.com.br)'
            )
        )
        .default([]),

      diaVencimentoFatura: Yup.number()
        .nullable()
        .notRequired()
        .integer('O dia deve ser um número inteiro')
        .min(1, 'O dia deve ser entre 1 e 31')
        .max(31, 'O dia deve ser entre 1 e 31'),
      faturamentoMinimo: Yup.number()
        .nullable()
        .notRequired()
        .integer('O quantidade mínima deve ser um número inteiro')
        .min(1, 'O valor precisa ser maior de 1'),
      valorAtendimento: Yup.number()
        .nullable()
        .notRequired()
        .min(1, 'O valor precisa ser maior que R$1'),
    },
    // nomeCnes e cnes dependem um do outro; sem isso o Yup acusa dependência cíclica
    [['nomeCnes', 'cnes']]
  )
}
